// shared-lib/labor/axes.js
//
// Every QUALIFIER AXIS declared once (claude-docs/2026-10-21-standard-variations-design.md,
// Part 1): the one place that says, for a fact that can vary about a priced
// thing, what its values are, how each reads, where the design supplies the
// answer, and — through its EFFECT — what an unpriced value carries.
//
//   look   the same work in another style: the contractor's base price
//          carries, silently (a baseboard style)
//   extra  real extra work: their base price carries — or, for an ORDERED
//          axis (a size band), their nearest lower priced band — FLAGGED,
//          unless an add-on or a row of theirs covers it
//   job    a different job that shares a key stem: never carried; their row
//          for that job, else the system's default for it
//
// `baseSynonyms` (optional) are words that name the axis at its BASE band
// or value — a fact for finding the item, never a variant: a pan "up to
// 60″", "frameless" glass.
// `synonyms` are the words in a contractor's line that name each value, for
// the importer (`price-import/jobtreadMapping.js` `qualifiersOf`): a pattern
// read in the line's NAME, or with `reads: 'all'` its name, description and
// cost group. An axis with none is one the importer does not read yet. The
// importer keeps only the axes of the item a line lands on
// (`relevantQualifiers`), so "modern" on a tile line names nothing.
//
// The order of this list is the order values are joined in a variant key
// (`tile-floor~large`, `set-shower-pan-enclosure~over-60x36~prefab`), so it
// never changes: a new axis is appended at the END.
//
// One axis per fact, and the base value of each axis carries NO qualifier (an
// alcove tub, a wall-mount filler, a swing door): a row with no qualifier
// prices exactly as it did before variants existed. Unknown is the base too.
// The base is never listed in `values`. Pure; requires nothing in this folder.

const EFFECTS = Object.freeze({ LOOK: 'look', EXTRA: 'extra', JOB: 'job' });

// Where the design records the answer — which family of readers supplies it.
const SOURCES = Object.freeze([
  'texture',
  'object',
  'opening',
  'scope',
  'fabrication',
  'trim',
  'relocation',
  // a wall's own height against its room's ceiling (`wallWork.ponyWallOf`)
  'wall',
]);

// a line that names a solid-surface or acrylic wall system, before or after
// the word it guards (the `piece` axis reads only such a line)
const SOLID_SURFACE = String.raw`\b(?:acrylic|onyx|sentrel|wilsonart|cultured\s+marble|solid[\s-]*surface)\b`;
const SOLID_SURFACE_NAMED = `(?:(?<=${SOLID_SURFACE}.*)|(?=.*${SOLID_SURFACE}))`;

// hung on the wall, and the fixture it is said of: a toilet's is the mount
// axis's, a sink's the basin's, and a faucet's neither (2026-10-25)
const WALL_HUNG = /\bwall[\s-]*(?:hung|mount(?:ed)?)\b/;
const TOILET = /\b(?:toilets?|commodes?|water\s+closets?|wc)\b/;
const BASIN = /\b(?:sinks?|lavs?|lavator(?:y|ies)|basins?)\b/;
/**
 * `word` (a regex source carrying its own `\\b`s) read only in a line that
 * also says one of `context` — before it or after it. The match is the word
 * alone, so it explains, and for another item blanks, only itself: a
 * shower's "sliding" never takes "Frameless Shower Glass" with it.
 */
const beside = (context, word) =>
  new RegExp(
    String.raw`(?<=\b(?:${context})\b.*)(?:${word})|(?:${word})(?=.*\b(?:${context})\b)`,
    'i'
  );
const GLASS_CONTEXT = 'glass|shower';
const SEAT_CONTEXT = 'benche?s?|seats?';
const NICHE_CONTEXT = 'niches?';
// a drain or a supply moved (the `fixture` axis)
const RELOCATION_CONTEXT = 'drains?|suppl(?:y|ies)';
const TUB_CONTEXT = '(?:bath)?tubs?';
const TOP_CONTEXT = 'counter[\\s-]*tops?|tops?';
const RECESS_CONTEXT =
  'recess(?:ed)?|curb[\\s-]*less|countersink|zero[\\s-]*curb';
// "over 15 ft²", "more than 40 sq ft", "larger than 15 SF"
const OVER_SQFT = (n) =>
  String.raw`\b(?:over|more\s+than|larger\s+than|above)\s*${n}\s*(?:ft²|ft2|sq\.?\s*ft\.?|sf|square\s+f(?:ee|oo)t)(?![a-z])`;

const declare = (d) =>
  Object.freeze({
    ...d,
    values: Object.freeze([...d.values]),
    words: Object.freeze({ ...d.words }),
    synonyms: Object.freeze(d.synonyms.map((x) => Object.freeze({ ...x }))),
    baseSynonyms: Object.freeze(
      (d.baseSynonyms ?? []).map((x) => Object.freeze({ ...x }))
    ),
  });

const AXIS_DECLARATIONS = Object.freeze(
  [
    {
      // a tile PIECE's size (`qualifiersForTexture`): one whose longest side is
      // over 24", or a MOSAIC set on its sheet (2026-10-25: `isMosaic` on the
      // finish) — a piece size, which NCE sets at a rate of its own (2026
      // p.289), not a laying pattern
      key: 'format',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'texture',
      values: ['large', 'mosaic'],
      words: { large: 'large format', mosaic: 'mosaic' },
      synonyms: [
        { value: 'large', pattern: /\blarge[\s-]*format\b/i },
        { value: 'mosaic', pattern: /\bmosaics?\b/i },
      ],
    },
    {
      // a SIZE BAND, smallest first; each item declares its own ordered
      // subset (a shower pan over 60×36; a mirror over 2′×3′, over 3′×5′).
      // The base is the smallest band. New values are appended: the values
      // are distinct strings, so two items' bands never collide.
      key: 'size',
      effect: EFFECTS.EXTRA,
      ordered: true,
      source: 'object',
      // a shower niche's bands by its longest side (2026-10-25): a single
      // niche fits within 16″, a two-tier within 28″, a three-tier beyond
      // …and a curbless recess by the pan's area: up to 15 ft², up to 40
      values: [
        'over-60x36',
        'over-2x3',
        'over-3x5',
        'over-16in',
        'over-28in',
        'over-15sqft',
        'over-40sqft',
      ],
      words: {
        'over-60x36': 'over 60×36',
        'over-2x3': 'over 2′×3′, up to 3′×5′',
        'over-3x5': 'over 3′×5′',
        'over-16in': 'over 16″, up to 28″ (two-tier)',
        'over-28in': 'over 28″ (three-tier)',
        'over-15sqft': 'over 15 ft², up to 40 ft²',
        'over-40sqft': 'over 40 ft²',
      },
      synonyms: [
        {
          value: 'over-60x36',
          pattern:
            /\b(over|larger\s+than|bigger\s+than|more\s+than|above)\s*60(?!\d)|\b60"?\s*(x\s*36"?\s*)?(and|or)\s*(larger|over|bigger)\b/i,
        },
        // a niche's tiers, or its shelves, beside "niche"
        {
          value: 'over-16in',
          pattern: beside(
            NICHE_CONTEXT,
            String.raw`\b(?:two|2|double)[\s-]*(?:tier(?:s|ed)?|shel(?:f|ves))\b`
          ),
        },
        // a curbless recess's area bands, beside a word of the recess
        {
          value: 'over-15sqft',
          pattern: beside(RECESS_CONTEXT, OVER_SQFT(15)),
        },
        {
          value: 'over-40sqft',
          pattern: beside(RECESS_CONTEXT, OVER_SQFT(40)),
        },
        {
          value: 'over-28in',
          pattern: beside(
            NICHE_CONTEXT,
            String.raw`\b(?:three|3|triple)[\s-]*(?:tier(?:s|ed)?|shel(?:f|ves))\b`
          ),
        },
      ],
      // words that name this axis at its BASE band — "Shower Pan up to 60\"",
      // "60x36 or Under": a fact about the size, choosing no variant, that
      // helps find the item that varies on it (the importer's word match)
      baseSynonyms: [
        {
          // the band below `over-60x36` (a shower pan's), never a mirror's
          below: 'over-60x36',
          pattern:
            /\b(up\s+to|under|less\s+than)\s*60(?!\d)|\b60"?\s*(x\s*36"?\s*)?or\s+(under|less|smaller)\b/i,
        },
        {
          // a single niche, the band below a two-tier one
          below: 'over-16in',
          pattern: beside(
            NICHE_CONTEXT,
            String.raw`\b(?:single|one|1)[\s-]*tier(?:ed)?\b`
          ),
        },
      ],
    },
    {
      // a tub that is not an alcove tub (`tubType`)
      key: 'tub',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      // a walk-in tub, its door sealed (2026-10-25), appended
      values: ['freestanding', 'drop-in', 'undermount', 'walk-in'],
      words: {
        freestanding: 'freestanding',
        'drop-in': 'drop-in',
        undermount: 'undermount',
        'walk-in': 'walk-in',
      },
      // "Freestanding" is a tub type AND a filler mount; the item a line lands
      // on keeps the axis it has variants on (`relevantQualifiers`).
      synonyms: [
        { value: 'freestanding', pattern: /\bfree[\s-]*standing\b/i },
        { value: 'drop-in', pattern: /\bdrop[\s-]*in\b/i },
        { value: 'undermount', pattern: /\bunder[\s-]*mount(ed)?\b/i },
        // a walk-in BATH or tub, never a walk-in shower or closet: the words
        // beside a tub, either side ("Bathtub, walk-in"), or "walk-in bath"
        {
          value: 'walk-in',
          pattern: new RegExp(
            `${beside(TUB_CONTEXT, String.raw`\bwalk[\s-]*in\b`).source}|\\bwalk[\\s-]*in\\s+bath\\b`,
            'i'
          ),
        },
      ],
    },
    {
      // where a thing is MOUNTED, where that is extra work: a tub filler not
      // on the wall (floor, deck), and a vent hood that is not a plain canopy
      // (2026-10-25) — a WALL chimney hood or an ISLAND hood hung from the
      // ceiling (`fixture-types/hoodForm`, else the catalog product's
      // `mountType` / `subtype`) — a medicine cabinet RECESSED in the wall
      // rather than hung on it, and a toilet hung on the wall, on its in-wall
      // carrier (2026-10-25). Each item declares the values it takes.
      key: 'mount',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['floor', 'deck', 'chimney', 'island', 'recessed', 'wall-hung'],
      words: {
        floor: 'floor-mount',
        deck: 'deck-mount',
        chimney: 'wall chimney',
        island: 'island, hung from the ceiling',
        recessed: 'recessed in the wall',
        'wall-hung': 'wall-hung with carrier',
      },
      synonyms: [
        {
          value: 'floor',
          pattern: /\bfree[\s-]*standing\b|\bfloor[\s-]*mount(ed)?\b/i,
        },
        { value: 'deck', pattern: /\broman\b|\bdeck[\s-]*mount(ed)?\b/i },
        { value: 'chimney', pattern: /\bchimney\b/i },
        // an island's HOOD only: "Island Cabinets" name no mount
        {
          value: 'island',
          pattern:
            /\bisland\s+(?:(?:range|vent)\s+)?hoods?\b|\bceiling[\s-]*(?:hung|mount(?:ed)?)\s+(?:(?:range|vent)\s+)?hoods?\b/i,
        },
        // only beside a cabinet: a recessed light is the light's
        {
          value: 'recessed',
          pattern:
            /\brecessed\b(?=.*\b(?:medicine|cabinets?)\b)|(?<=\b(?:medicine|cabinets?)\b.*)\brecessed\b/i,
        },
        // only beside a toilet: a wall-hung sink is the basin's
        {
          value: 'wall-hung',
          pattern: new RegExp(
            `${WALL_HUNG.source}(?=.*${TOILET.source})|(?<=${TOILET.source}.*)${WALL_HUNG.source}`,
            'i'
          ),
        },
      ],
    },
    {
      // a door that does not swing (`category.door.doorType`)
      key: 'door',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'opening',
      // barn (a surface-track slab) and bifold, appended (Phase B)
      // and an exterior door with sidelites (2026-10-25)
      values: ['pocket', 'sliding', 'french', 'barn', 'bifold', 'sidelites'],
      words: {
        pocket: 'pocket',
        sliding: 'sliding',
        french: 'French',
        barn: 'barn (surface track)',
        bifold: 'bifold',
        sidelites: 'sidelites',
      },
      synonyms: [
        { value: 'pocket', pattern: /\bpocket\b/i },
        { value: 'sliding', pattern: /\bsliding\b|\bslider\b|\bbypass\b/i },
        { value: 'french', pattern: /\bfrench\b/i },
        { value: 'barn', pattern: /\bbarn\b/i },
        { value: 'bifold', pattern: /\bbi-?fold\b/i },
        { value: 'sidelites', pattern: /\bside[\s-]*li(?:te|ght)s?\b/i },
      ],
    },
    {
      // a shower valve in a tub's fixture group, so it carries a diverter —
      // and sets the tub's spout with it (2026-10-28, `fixtureParts.js`)
      key: 'valve',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['diverter'],
      words: { diverter: 'tub/shower, with its spout' },
      // an INTERNAL diverter sends water to a second head, not to a tub
      // spout (`heads`)
      synonyms: [
        { value: 'diverter', pattern: /(?<!\binternal\s)\bdiverter\b/i },
        // a tub-shower's valve, or one said to set the spout with it
        {
          value: 'diverter',
          pattern:
            /\btub\s*\/\s*shower\b|\bwith\s+(?:its\s+|a\s+|the\s+)?(?:tub\s+)?spout\b/i,
        },
      ],
    },
    {
      // a RECESSED can rather than a surface ceiling light (2026-10-15): a
      // housing cut in and wired, or with `install` a retrofit where a can
      // stands.
      key: 'light',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      // a CHANDELIER (2026-10-25, `lightingSubtype`), appended: heavier, and
      // assembled and hung from a braced box
      values: ['recessed', 'chandelier'],
      words: { recessed: 'recessed', chandelier: 'chandelier' },
      // a can or pot light is a recessed one
      synonyms: [
        {
          value: 'recessed',
          pattern: /\brecessed\b|\bcan\s+lights?\b|\bpot\s+lights?\b/i,
        },
        { value: 'chandelier', pattern: /\bchandeliers?\b/i },
      ],
    },
    {
      // an electrical device that REPLACES one where it stands, rather than
      // one in a new location (claude-docs/2026-10-06-supply-and-devices-plan.md)
      // — a device swapped, against a new box and run: another job.
      key: 'install',
      effect: EFFECTS.JOB,
      ordered: false,
      source: 'object',
      values: ['replace'],
      words: { replace: 'replacing an existing one' },
      // a device swapped where one stands: "R/R Switch", "Change Out GFCI
      // Plug", "Replacing existing outlet"
      synonyms: [
        {
          value: 'replace',
          pattern:
            /\bR\s*(\/|&|and)\s*R\b|\bremove\s+(and|&)\s+replace\b|\breplac(e|es|ed|ing|ement)\b|\bchange[\s-]*out\b/i,
        },
        // a window's INSERT, set in its old frame (2026-10-25)
        {
          value: 'replace',
          pattern:
            /\binserts?\b(?=.*\bwindows?\b)|(?<=\bwindows?\b.*)\binserts?\b/i,
        },
      ],
    },
    {
      // a shower's PREFAB pan (acrylic, fiberglass, solid surface) rather
      // than a tiled one (claude-docs/2026-10-07-shower-pans-plan.md) — set,
      // against one built: another job. Tiled is the base.
      key: 'pan',
      effect: EFFECTS.JOB,
      ordered: false,
      source: 'object',
      values: ['prefab'],
      words: { prefab: 'prefab pan' },
      // Read over the description and cost group too (`reads: 'all'`) —
      // "Install Shower Pan 60x36 or Under" says acrylic only there — which no
      // other fact is: a description is free text, and "not larger than 12x24"
      // must not read as large format.
      synonyms: [
        {
          value: 'prefab',
          pattern:
            /\b(acrylic|fiberglass|sentrel|onyx|wilsonart|solid[\s-]*surface|prefab)\b/i,
          reads: 'all',
        },
      ],
    },
    {
      // the pan that was there BEFORE, for its demolition — an acrylic pan
      // torn out, against a mortar bed: another job. Tiled is the base.
      key: 'found',
      effect: EFFECTS.JOB,
      ordered: false,
      source: 'scope',
      values: ['acrylic-pan'],
      words: { 'acrylic-pan': 'acrylic pan' },
      // the pan a demolition line tears out
      synonyms: [{ value: 'acrylic-pan', pattern: /\bacrylic\b/i }],
    },
    {
      // The BRANCH DRAIN a relocated fixture's run is made of. 1.5" is the
      // base (a lavatory, a kitchen sink, a dishwasher, a bidet); a shower, a
      // tub, a washer and a urinal run 2"; a water closet runs 3". Sizes
      // written without the dot a price-book key may not contain. Ordered in
      // fact, but NOT declared ordered: a band would carry a 2" price onto a
      // 3" drain, which changes a price today (design, MY CALL 7).
      key: 'drain',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'relocation',
      values: ['2in', '3in'],
      words: { '2in': '2″', '3in': '3″' },
      // the size written before the drain it sizes: 3" drain, 2 inch waste
      // …or after it: "drain 3 inch", "Relocate drain — 2″" (a length after
      // it — "Drain, 3 lf minimum" — is no size). A drain's, or a waste
      // line's: a vent or a supply line, or a fraction ("1/2\" line"), is no
      // drain size.
      synonyms: [
        {
          value: '2in',
          pattern:
            /(?<![\d/.])\b2(\.0)?\s*(″|"|-?\s*inch(es)?|in\.?)\s*(drain|waste)|\b(drain|waste)(\s+line)?\b[\s,:—–-]*2(\.0)?\s*(″|"|-?\s*inch(es)?\b|in\.(?!\w)|in$)/i,
        },
        {
          value: '3in',
          pattern:
            /(?<![\d/.])\b3(\.0)?\s*(″|"|-?\s*inch(es)?|in\.?)\s*(drain|waste)|\b(drain|waste)(\s+line)?\b[\s,:—–-]*3(\.0)?\s*(″|"|-?\s*inch(es)?\b|in\.(?!\w)|in$)/i,
        },
      ],
    },
    {
      // A stone top's EDGE profile, for the feet of it a fabricator polishes
      // (reference-docs/estimates/overview.md, "Countertop fabrication"). The
      // plain polished edge is the base; a profile adds work to it.
      key: 'edge',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'fabrication',
      // …and a tiled edge's finish (2026-10-25, `install-tile-edge`): a
      // bullnose piece, or a METAL profile set in the thin-set, appended
      values: ['beveled', 'bullnose', 'ogee', 'metal'],
      words: {
        beveled: 'beveled',
        bullnose: 'bullnose',
        ogee: 'ogee',
        metal: 'metal profile',
      },
      synonyms: [
        { value: 'beveled', pattern: /\bbevel+ed\b|\bbevel\b/i },
        { value: 'bullnose', pattern: /\bbull[\s-]*nose\b/i },
        { value: 'ogee', pattern: /\bogee\b/i },
        // a Schluter strip, or a metal trim, edge or profile
        {
          value: 'metal',
          pattern:
            /\bschluter\b|\b(?:metal|aluminum|aluminium|stainless)\s+(?:edge|trim|profile)s?\b/i,
        },
      ],
    },
    {
      // the SINK CUTOUT a basin needs: the drop-in cutout is the base; an
      // undermount's polished cutout adds work to it
      key: 'cutout',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'fabrication',
      values: ['undermount'],
      words: { undermount: 'undermount, polished' },
      // a countertop cut out for an undermount sink is polished (#14)
      synonyms: [
        { value: 'undermount', pattern: /\bunder[\s-]*mount(ed)?\b/i },
      ],
    },
    {
      // the STYLE of baseboard a wall's trim draws (`wall.trim.style`,
      // 2026-10-16) — a contractor prices a 4" and a 6" board apart. A wall
      // with no style set is the base: unknown is not a style. A LOOK (the
      // user's decision): the same work in another style. Words carry the
      // board height the 3D view draws for each (`useWallTrim`).
      key: 'baseboard',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'trim',
      values: ['traditional', 'modern', 'minimal'],
      words: {
        traditional: 'traditional 5″',
        modern: 'modern 3″',
        minimal: 'minimal 2″',
      },
      // a colonial board is the traditional profile
      synonyms: [
        { value: 'traditional', pattern: /\btraditional\b|\bcolonial\b/i },
        { value: 'modern', pattern: /\bmodern\b/i },
        { value: 'minimal', pattern: /\bminimal\b/i },
      ],
    },
    {
      // a room ABOVE the ground floor (`floorLevel.js`: the room's storey
      // moved by the scan's lowest-floor setting) — a tub carried up a flight
      // of stairs. Ground, a basement and unknown are the base. Tubs only for
      // now (design, MY CALL 39).
      key: 'story',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['upper'],
      words: { upper: 'upper floor' },
      // "second story", "2nd floor", "upper level", "upstairs"; "1st floor"
      // names nothing — it is the base
      synonyms: [
        {
          value: 'upper',
          pattern:
            /\b(second|2nd|third|3rd|upper)\s+(story|storey|floor|level)\b|\bupstairs\b/i,
        },
      ],
    },
    {
      // a shower whose VALVE stays in the wall: only its trim is set
      // (`trimOnly` on a valve part, the panel's "Existing valve stays — trim
      // only"). Less work than valve and trim, so a different JOB — it cannot
      // share `valve`, whose diverter is extra (an axis has one effect).
      key: 'retrofit',
      effect: EFFECTS.JOB,
      ordered: false,
      source: 'object',
      values: ['trim-only'],
      words: { 'trim-only': 'trim only, on an existing valve' },
      // "trim only", "reinstall shower trim"
      synonyms: [
        {
          value: 'trim-only',
          pattern:
            /\btrim\s+only\b|\bre-?install\w*\s+(the\s+)?(shower\s+)?trim\b/i,
        },
      ],
    },
    {
      // the STONE a slab is: quartzite is cut, polished and set slower than
      // quartz (the base, with marble, anything else and unknown); granite
      // (2026-10-25) is priced apart by contractors who sell it beside
      // quartz, so one bundle or price per stone can be used. Read only from
      // a slab finish's `material` (`qualifiersForTexture`), never a name —
      // the catalog has "Quartzite Look" porcelain tiles (design MY CALL 45).
      key: 'stone',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'texture',
      values: ['quartzite', 'granite'],
      words: { quartzite: 'quartzite', granite: 'granite' },
      // the importer reads a contractor's NAME, where "quartzite" or
      // "granite" means the stone (a stone-look tile names no item with a
      // stone axis)
      synonyms: [
        { value: 'quartzite', pattern: /\bquartzite\b/i },
        { value: 'granite', pattern: /\bgranite\b/i },
      ],
    },
    {
      // a PART of a stone top set apart: its waterfall legs, the top's stone
      // turned down to the floor at an open end (`waterfallLegAreaSqM`) — a
      // variant of the top's install, not an item of its own (design MY CALL
      // 48), so a countertop price carries onto them exactly as it billed
      // them before.
      key: 'section',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'fabrication',
      values: ['waterfall'],
      words: { waterfall: 'waterfall legs' },
      synonyms: [{ value: 'waterfall', pattern: /\bwaterfall\b/i }],
    },
    {
      // the kind of opening a new wall's rough opening frames
      // (`estimate-pricing/utils/wallWork.js`): a door or passage up to 3'
      // wide is the base, a wider one or a window takes a bigger header and,
      // for a window, a sill and cripples (NCE 2026 pp. 43–44). "Wide" is
      // read only as over 3': a line reading "up to 3' wide" names the base.
      key: 'opening',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'opening',
      values: ['wide', 'window'],
      words: { wide: "over 3' wide", window: 'window' },
      synonyms: [
        {
          value: 'wide',
          pattern: /\b(?:over|wider than)\s+3\s*(?:'|ft\b|feet\b)/i,
        },
        // a window's ROUGH OPENING: "window" beside "rough" or "opening" —
        // only the word itself is the fact's, so "Install Window", "Remove
        // Window" and "Close Window Opening" keep it as a word of their own
        // (2026-10-25: read anywhere, it took "window" from every window
        // item)
        {
          value: 'window',
          pattern:
            /\bwindows?\b(?=.*\b(?:rough|openings?)\b)|(?<=\b(?:rough|openings?)\b.*)\bwindows?\b/i,
        },
      ],
    },
    {
      // the kind of trim being PAINTED (`estimate-pricing/utils/
      // millworkTakeOffs.js` keeps each painted length's kind): baseboard and
      // casing are the base; crown is painted overhead, cut in against the
      // ceiling. A LOOK, not extra work, on purpose (2026-10-23): a book that
      // prices trim painting but not crown carries its trim rate silently,
      // exactly as crown was billed before the variant existed — an `extra`
      // would flag every estimate with crown for every such contractor.
      key: 'trim',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'trim',
      values: ['crown'],
      words: { crown: 'crown molding' },
      synonyms: [{ value: 'crown', pattern: /\bcrown\b/i }],
    },
    {
      // the floor finish a demolition takes up (`demoScope.js` reads it from
      // the job's answer, else the inspection): carpet, laminate and vinyl
      // plank are the base; a wood floor is nailed or glued down, and a sheet
      // vinyl or linoleum floor glued down (2026-10-25). The same fact names
      // the vinyl floor LAID as a sheet (`vinyl-floor~sheet-vinyl`, read from
      // the finish, `qualifiersForTexture`, 2026-10-25). A LOOK, like crown
      // (2026-10-24): a book that prices floor removal but not wood carries
      // its rate silently, as wood floors were billed before the variant
      // existed — the floor that was ASSUMED, not seen, is called out on the
      // estimate line instead (`assumedFloors`).
      key: 'floor',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'scope',
      values: ['wood', 'sheet-vinyl'],
      words: { wood: 'wood floor', 'sheet-vinyl': 'sheet vinyl or linoleum' },
      // never laminate "wood", a floating floor that lifts like vinyl (NCE
      // 2026 p.101), nor a "wood-look" tile or plank
      synonyms: [
        {
          value: 'wood',
          pattern: /(?<!\blaminated?\s+)\b(?:hard\s*)?wood\b(?![\s-]*look)/i,
        },
        // a glued sheet: "sheet vinyl", "vinyl sheet", "linoleum" — never
        // vinyl alone, which is as often plank, the base
        {
          value: 'sheet-vinyl',
          pattern:
            /\bsheet[\s-]*vinyl\b|\bvinyl[\s-]*sheet(?:ing)?\b|\blinoleum\b/i,
        },
      ],
    },
    {
      // a door WIDER than the passage widths (2026-10-25): over 30″ — a 32″
      // or 36″ door, the common break between a standard and a wide door —
      // read at the door schedule's whole inch from the door's dimensions
      // (`qualifiersForDoor`). A LOOK, like crown and the floor: NCE 2026
      // p.118 hangs every single prehung door from 24″ to 36″ at the same
      // BC@.750, so by the costbook a wide door is the same work. The value
      // is there for a contractor who prices one apart; a book that prices
      // only the plain hang carries it silently, where an `extra` would flag
      // every estimate with a 32″ door.
      key: 'width',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'opening',
      values: ['over-30in'],
      words: { 'over-30in': 'over 30″ wide' },
      // "over 30″", "wider than 30 inches", or a 32″ to 36″ width ("36″
      // Door", "32 inch"); a bare "large" says nothing of a door
      synonyms: [
        {
          value: 'over-30in',
          pattern:
            /\b(?:over|wider\s+than|larger\s+than|more\s+than)\s*30\s*(?:"|″|in\b|-?\s*inch(?:es)?\b)|\b3[2-6]\s*(?:"|″|in\b|-?\s*inch(?:es)?\b)/i,
        },
      ],
    },
    {
      // a range or cooktop that burns GAS (2026-10-25): its catalog product's
      // `fuelType` is gas, dual-fuel (a gas top) or propane
      // (`qualifiersForModel`). Not a variant of anything: the fact ADDS an
      // item, the gas connection (`connect-gas-appliance`, a plumber's), to
      // the range's own set and hookup (`takeoffMapping.FACT_WORK`). Electric,
      // induction and an unknown fuel are the base. No synonyms: a line's
      // "gas" is read by the word match, which keeps a gas line off every
      // item that does not say gas (`CARRIED`).
      key: 'fuel',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['gas'],
      words: { gas: 'gas' },
      synonyms: [],
    },
    {
      // A PONY wall (user ruling, 2026-10-25): a new wall that stops short of
      // its room's ceiling, read from its own top against the ceiling over it
      // (`wallWork.ponyWallOf`) — no height cutoff. A LOOK: framing one takes
      // the per-foot hours a full wall does ("No height variant" of the
      // framing hours), so a book that prices framing by the foot carries its
      // rate silently; the variant is there so a price PER PONY WALL
      // (`frame-wall~pony`, counted each, `unitConversions.js`) bills only
      // those.
      key: 'height',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'wall',
      values: ['pony'],
      words: { pony: 'pony wall' },
      // "pony wall", "half wall" — never a knee wall, which meets its slope
      synonyms: [{ value: 'pony', pattern: /\b(?:pony|half)[\s-]*walls?\b/i }],
    },
    {
      // what a shower's GLASS is (2026-10-25, `install-shower-glass`): fixed
      // panels with a hinged door is the base, what the design draws on every
      // shower; fixed panels alone (a splash shield), a hinged door alone in
      // an opening, or a sliding door are each priced apart by the ft².
      // Read only in a contractor's line, and only beside "glass" or
      // "shower": a fact the words name nowhere else.
      key: 'glass',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['fixed', 'hinged-door', 'slider'],
      words: {
        fixed: 'fixed panel, no door',
        'hinged-door': 'hinged door alone',
        slider: 'sliding door',
      },
      synonyms: [
        {
          value: 'fixed',
          pattern: beside(
            GLASS_CONTEXT,
            String.raw`\b(?:fixed\s+(?:glass\s+)?panels?|shields?|splash\s*(?:guards?|panels?)|screens?)\b`
          ),
        },
        {
          value: 'hinged-door',
          pattern: beside(
            GLASS_CONTEXT,
            String.raw`\b(?:hinged|swing(?:ing)?|pivot)\s+(?:glass\s+|shower\s+)*doors?\b`
          ),
        },
        {
          value: 'slider',
          pattern: beside(
            GLASS_CONTEXT,
            String.raw`\b(?:slid(?:er|ing)|by-?pass(?:ing)?)\b`
          ),
        },
      ],
    },
    {
      // how a shower's glass is FRAMED (2026-10-25, the shower's
      // `glassFraming`): frameless is the base; a framed or semi-frameless
      // enclosure is a LOOK — NCE 2026 p.271 hangs a framed door and a
      // frameless one at the same BG@1.57 — so a book that prices only
      // frameless glass carries its rate onto them silently. Unknown is the
      // base, though the 3D view draws an unset shower framed.
      key: 'framing',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'object',
      values: ['semi-frameless', 'framed'],
      words: { 'semi-frameless': 'semi-frameless', framed: 'framed' },
      synonyms: [
        { value: 'semi-frameless', pattern: /\bsemi[\s-]*frameless\b/i },
        {
          value: 'framed',
          pattern: beside(
            GLASS_CONTEXT,
            String.raw`(?<!semi[\s-]?|un)\bframed\b`
          ),
        },
      ],
      // "Frameless Shower Glass" names the framing at its base: a fact for finding
      // the item, never a variant (the word match's `baseNamed`)
      baseSynonyms: [
        {
          below: 'semi-frameless',
          pattern: /(?<!semi[\s-]*)\bframeless\b/i,
        },
      ],
    },
    {
      // how a shower SEAT is built (2026-10-25, `install-shower-seat`): a
      // seat hung on the wall is the base; a solid-surface or acrylic corner
      // seat set floating, a fold-down seat, a framed seat with a top, a seat
      // framed and tiled each priced apart. Typed per job, and read in a line
      // beside "bench" or "seat"; a shower system's molded seat in a design
      // (`shower-seat`, shower systems step 3a) reads `floating` off the
      // object (`qualifiers.js`).
      key: 'seat',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'scope',
      values: ['floating', 'fold-down', 'built-in', 'tiled'],
      words: {
        floating: 'floating',
        'fold-down': 'fold-down',
        'built-in': 'built-in',
        tiled: 'tiled',
      },
      synonyms: [
        {
          value: 'floating',
          pattern: beside(SEAT_CONTEXT, String.raw`\bfloating\b`),
        },
        {
          value: 'fold-down',
          pattern: beside(
            SEAT_CONTEXT,
            String.raw`\bfold(?:ing|[\s-]*(?:down|up))\b`
          ),
        },
        {
          value: 'built-in',
          pattern: beside(SEAT_CONTEXT, String.raw`\bbuilt[\s-]*in\b`),
        },
        {
          value: 'tiled',
          pattern: beside(SEAT_CONTEXT, String.raw`\btiled?\b`),
        },
      ],
    },
    {
      // a shower NICHE set as a prefab insert — acrylic, solid surface, metal
      // — rather than framed, waterproofed and tiled (2026-10-25): another
      // job. The niche's panel records it (`nicheBuild`, 2026-10-28).
      key: 'niche',
      effect: EFFECTS.JOB,
      ordered: false,
      source: 'object',
      values: ['prefab'],
      words: { prefab: 'prefab insert' },
      synonyms: [
        {
          value: 'prefab',
          pattern: beside(
            NICHE_CONTEXT,
            String.raw`\b(?:acrylic|sentrel|onyx|wilsonart|metal|stainless|steel|prefab(?:ricated)?|inserts?|solid[\s-]*surface)\b`
          ),
        },
      ],
    },
    {
      // a shower niche WIDER than a stud bay (2026-10-25): a stud cut and
      // headed, read from the niche's width (`qualifiers.js`). A niche turned
      // on its side is the common one; "horizontal" is a contractor's word for
      // it, beside "niche".
      key: 'orientation',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['horizontal'],
      words: { horizontal: 'horizontal, across a stud' },
      synonyms: [
        {
          value: 'horizontal',
          pattern: beside(NICHE_CONTEXT, String.raw`\bhorizontal\b`),
        },
      ],
    },
    {
      // a niche's back set in a MOSAIC, an accent against its field tile
      // (2026-10-25): extra work on the niche, beside "niche". The niche's
      // panel records it (`nicheBackTextureId`, 2026-10-28).
      key: 'accent',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['mosaic-back'],
      words: { 'mosaic-back': 'mosaic back' },
      synonyms: [
        {
          value: 'mosaic-back',
          pattern: beside(NICHE_CONTEXT, String.raw`\bmosaic\b`),
        },
      ],
    },
    {
      // what a TUB is made of (2026-10-25): a cast-iron tub is carried in and
      // set heavier than the steel or acrylic base. Read in a line's name
      // beside a tub; nothing in the design or the catalog records a tub's
      // material yet. Upstairs, a pair with `story`.
      key: 'material',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['cast-iron'],
      words: { 'cast-iron': 'cast iron' },
      synonyms: [
        {
          value: 'cast-iron',
          pattern: beside(
            `${TUB_CONTEXT}|bath`,
            String.raw`\bcast[\s-]*iron\b`
          ),
        },
      ],
    },
    {
      // the PART of a tub platform framed (2026-10-25): its skirt alone — the
      // apron along the tub's face, no deck — against the whole platform, the
      // base: another job. A skirt beside a deck is the platform.
      key: 'platform',
      effect: EFFECTS.JOB,
      ordered: false,
      source: 'object',
      values: ['skirt'],
      words: { skirt: 'skirt only' },
      synonyms: [
        { value: 'skirt', pattern: /^(?!.*\bdecks?\b).*?\b(skirts?)\b/i },
      ],
    },
    {
      // a shower valve feeding a SECOND head — a hand shower or a rain head
      // beside the fixed one, through a diverter inside the valve
      // (2026-10-25). Extra work: the second head's riser. Not read from the
      // design yet: a shower drawn with two heads already bills two valve
      // installs (`ASSEMBLY_PARTS` counts the largest part), so reading it
      // waits on counting such a shower as one valve.
      key: 'heads',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['second-head'],
      words: { 'second-head': 'with a second head' },
      // "Internal Diverter" (a diverter between two heads, never a tub's),
      // "second head", "dual shower heads", "hand shower", "handheld"
      synonyms: [
        {
          value: 'second-head',
          pattern:
            /\binternal\s+diverter\b|\b(?:second|2nd|two|dual|extra)\s+(?:shower\s*)?heads?\b|\bhand[\s-]*(?:held|shower)\b/i,
        },
      ],
    },
    {
      // an outlet set INTO a counter or a floor (2026-10-25): the drawing's
      // `surface-outlet`, read off its type — a pop-up or a flush box cut
      // into the surface, against a box in a wall. Extra work.
      key: 'outlet',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['popup'],
      words: { popup: 'pop-up, set in a counter or floor' },
      synonyms: [
        {
          value: 'popup',
          pattern:
            /\bpop[\s-]*(?:up|out)\b|\b(?:floor|countertop|counter)\s+(?:outlet|receptacle|plug|box)s?\b/i,
        },
      ],
    },
    {
      // where a new exhaust fan's duct comes out (2026-10-25): the roof is
      // the base; a wall its own value. A LOOK: NCE 2026 p.141 sets a roof
      // kit and a through-wall kit in the same time, so a book with one price
      // for venting carries it silently.
      key: 'vent',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'object',
      values: ['wall'],
      words: { wall: 'through a wall' },
      // "Side Vent", "Wall Vent", "through the wall", "sidewall cap"
      synonyms: [
        {
          value: 'wall',
          pattern:
            /\bside(?:[\s-]*wall)?\s+(?:vent|cap|jack)s?\b|\bwall\s+(?:vent|cap|jack)s?\b|\b(?:thru|through)[\s-]+(?:the\s+|a\s+)?wall\b/i,
        },
      ],
    },
    {
      // a bath fan with a HEATER or heat lamp in it (2026-10-25): extra work
      key: 'fan',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['heater'],
      words: { heater: 'with a heater' },
      // only beside the fan it is in: "Install Heater" is a wall heater
      synonyms: [
        {
          value: 'heater',
          pattern:
            /\bfans?\b.*\bheat(?:ers?|[\s-]*lamps?)\b|\bheat(?:ers?|[\s-]*lamps?)\b.*\bfans?\b/i,
        },
      ],
    },
    {
      // a switch that is a timer or a humidity control (2026-10-25), a bath
      // fan's usually: extra work
      key: 'control',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['timer'],
      words: { timer: 'timer or humidity control' },
      synonyms: [
        {
          value: 'timer',
          pattern: /\btimers?\b|\bhumidi(?:ty|stat)\b/i,
        },
      ],
    },
    {
      // which PIECE of a solid-surface wall system's trim (2026-10-25): a
      // window's return, a corner, a crown, a band, a pony wall's cap, a
      // ramp (2026-10-31); flat trim is the base. A LOOK: one contractor's one trim price covers
      // every piece.
      key: 'piece',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'scope',
      values: ['window', 'corner', 'crown', 'band', 'cap', 'ramp'],
      words: {
        window: 'window trim',
        corner: 'corner',
        crown: 'crown',
        band: 'decorative band',
        cap: 'wall cap',
        ramp: 'ramp',
      },
      // only in a line naming a solid-surface system, and only the piece's
      // word: "Window Trim" alone is a window's wood trim, "Cap Plumbing" a
      // plumber's, "Decoration Band" tile
      synonyms: [
        ['window', /\bwindows?\b/],
        ['corner', /\bcorners?\b/],
        ['crown', /\bcrown\b/],
        ['band', /\bbands?\b/],
        ['cap', /\bcaps?\b/],
        // a solid-surface ramp at a curbless shower's entry, sold by the
        // foot (2026-10-31: "Onyx Ramp"); a mortar ramp is its own item
        ['ramp', /\bramps?\b/],
      ].map(([value, word]) => ({
        value,
        pattern: new RegExp(`${SOLID_SURFACE_NAMED}${word.source}`, 'i'),
      })),
    },
    {
      // tile LAID IN A PATTERN — herringbone, chevron, hexagon, basketweave
      // (2026-10-25): extra cutting and layout. Nothing in the design records
      // a laying pattern (`wasteFactor.js` says so), so it is typed per job
      // until a finish carries one. A mosaic is a piece size, not a layout.
      key: 'layout',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'texture',
      values: ['pattern'],
      words: { pattern: 'laid in a pattern' },
      synonyms: [
        {
          value: 'pattern',
          pattern:
            /\bpattern(?:ed)?\b|\bherringbone\b|\bchevron\b|\bhex(?:agon(?:al)?)?\b|\bbasket[\s-]*weave\b/i,
        },
      ],
    },
    {
      // painting a cabinet's INSIDE — its box, shelves and backs — rather
      // than its faces and doors (2026-10-25): another job
      key: 'inside',
      effect: EFFECTS.JOB,
      ordered: false,
      source: 'scope',
      values: ['interior'],
      words: { interior: 'inside the boxes' },
      // only beside "cabinet": an interior door is a door
      synonyms: [
        {
          value: 'interior',
          pattern:
            /\b(?:interiors?|insides?)\b(?=.*\bcabinets?\b)|(?<=\bcabinets?\b.*)\b(?:interiors?|insides?)\b/i,
        },
      ],
    },
    {
      // a cabinet's BOX construction: frameless (European) against the
      // face-framed base (2026-10-25). A LOOK: NCE 2026 p.55 installs a
      // cabinet at one rate whatever its box. The cabinet family records it
      // (`spec.construction`), but the generic families' "frameless" is the
      // way they are drawn, so it is not read yet.
      key: 'construction',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'object',
      values: ['frameless'],
      words: { frameless: 'frameless' },
      // only beside a cabinet: frameless glass or a frameless mirror is not
      synonyms: [
        {
          value: 'frameless',
          pattern:
            /\bframeless\b(?=.*\b(?:cabinet|cabinets|vanity|vanities)\b)|(?<=\b(?:cabinet|cabinets|vanity|vanities)\b.*)\bframeless\b/i,
        },
      ],
    },
    {
      // what a CROWN molding runs over: the room's wall top (the base) or a
      // run of cabinets (2026-10-25). A LOOK: NCE 2026 p.54 sets crown at one
      // rate wherever it runs. Cabinet crown is not measured: typed per job.
      key: 'over',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'scope',
      values: ['cabinet'],
      words: { cabinet: 'on cabinets' },
      // "cabinet" beside "crown" — only the word itself, so the crown's own
      // words stay a crown's
      synonyms: [
        {
          value: 'cabinet',
          pattern:
            /\bcabinets?\b(?=.*\bcrown\b)|(?<=\bcrown\b.*)\bcabinets?\b/i,
        },
      ],
    },
    {
      // a DOUBLE wall oven, two ovens stacked in one tall cabinet
      // (2026-10-25): heavier, set by two. No catalog field records it, so it
      // is typed per job.
      key: 'oven',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['double'],
      words: { double: 'double' },
      // only the word "double", and only beside an oven ("Double Bowl Sink"
      // is a sink's)
      synonyms: [
        {
          value: 'double',
          pattern:
            /\b(?:double|twin)(?=\s+(?:wall\s+)?ovens?\b)|(?<=\bovens?\b.*)\b(?:double|twin)\b/i,
        },
      ],
    },
    {
      // a home of TWO or more storeys, for work priced over the whole home
      // (a final clean: stairs and two levels to carry to, 2026-10-25) —
      // never one room's floor, which is `story`. Extra work; typed.
      key: 'stories',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'scope',
      values: ['two-story'],
      words: { 'two-story': 'two-story home' },
      // "2 story", "two-story", "multi-story" — never "2nd floor" or "second
      // story", a room's floor (`story`)
      synonyms: [
        {
          value: 'two-story',
          pattern: /\b(?:2|two|multi)[\s-]*stor(?:y|ey|ies)\b/i,
        },
      ],
    },
    {
      // insulation for SOUND, between rooms and floors, rather than heat
      // (2026-10-25)
      key: 'insulation',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'scope',
      values: ['sound'],
      words: { sound: 'sound batts' },
      synonyms: [
        {
          value: 'sound',
          pattern: /\b(?:sound|acoustic(?:al)?)(?=\s+(?:insulation|batts?)\b)/i,
        },
      ],
    },
    {
      // a TANKLESS water heater rather than a tank (2026-10-25): its venting
      // and a bigger gas line make it another job
      key: 'tank',
      effect: EFFECTS.JOB,
      ordered: false,
      source: 'scope',
      values: ['tankless'],
      words: { tankless: 'tankless' },
      synonyms: [{ value: 'tankless', pattern: /\btank[\s-]*less\b/i }],
    },
    {
      // WHICH framed built-in comes out (2026-10-25): a bench. A look: the
      // same framing torn out by the foot. A framed TUB DECK is the base: its
      // words are the tub platform's (`PHRASES`), and a value read from them
      // would take them from a tub platform's removal, each
      key: 'framed',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'scope',
      values: ['bench'],
      words: { bench: 'framed bench' },
      // only beside "framed": a shower's bench is a seat
      synonyms: [
        {
          value: 'bench',
          pattern:
            /\bbench(?:es)?\b(?=.*\bframed\b)|(?<=\bframed\b.*)\bbench(?:es)?\b/i,
        },
      ],
    },
    {
      // a PLASTER wall or ceiling rather than drywall (2026-10-25): slower to
      // take down and to patch. Extra work.
      key: 'wallboard',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'scope',
      values: ['plaster'],
      words: { plaster: 'plaster' },
      synonyms: [{ value: 'plaster', pattern: /\bplaster(?:ed)?\b/i }],
    },
    {
      // drywall on a CEILING rather than a wall (2026-10-25): overhead, NCE
      // adds a third. Typed: a design rebuilds no ceiling yet.
      key: 'plane',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'scope',
      values: ['ceiling'],
      words: { ceiling: 'on a ceiling' },
      // only the word itself, so the ceiling's own items keep it
      synonyms: [
        {
          value: 'ceiling',
          pattern:
            /\bceilings?\b(?=.*\b(?:drywall|sheetrock|wallboard|gypsum|tape|taping|mud)\b)|(?<=\b(?:drywall|sheetrock|wallboard|gypsum|tape|taping|mud)\b.*)\bceilings?\b/i,
        },
      ],
    },
    {
      // a BAY or bow window, its sections set out from the wall (2026-10-25):
      // extra work. Typed: the design draws windows flat in their wall.
      key: 'window',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'opening',
      values: ['bay'],
      words: { bay: 'bay or bow' },
      // only before "window": a Glacier Bay toilet is a toilet
      synonyms: [
        {
          value: 'bay',
          pattern:
            /\b(?:bay|bow)(?=\s+windows?\b)|(?<=\bwindows?\b.*)\b(?:bay|bow)\b/i,
        },
      ],
    },
    {
      // woodwork STAINED and clear-finished rather than painted (2026-10-25).
      // A LOOK: NCE 2026 p.238 stains a wood floor in a coat's time.
      key: 'coating',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'trim',
      values: ['stain'],
      words: { stain: 'stained' },
      synonyms: [{ value: 'stain', pattern: /\bstain(?:ed|ing|s)?\b/i }],
    },
    {
      // a mantel WITH its surround, pilasters and frieze (2026-10-25)
      key: 'mantel',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'scope',
      values: ['surround'],
      words: { surround: 'with surround' },
      // only beside a mantel: a tub surround is the tub's
      synonyms: [
        {
          value: 'surround',
          pattern:
            /\bsurrounds?\b(?=.*\bmantels?\b)|(?<=\bmantels?\b.*)\bsurrounds?\b/i,
        },
      ],
    },
    {
      // WIRE shelving kits rather than a wood shelf and pole (2026-10-25)
      key: 'shelving',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'scope',
      values: ['wire'],
      words: { wire: 'wire shelving' },
      // only before shelving or a shelf: wiring is an electrician's
      synonyms: [
        {
          value: 'wire',
          pattern:
            /\bwire(?=\s+(?:closet\s+)?(?:shel(?:f|ves|ving)|organizers?)\b)/i,
        },
      ],
    },
    {
      // a bathroom sink that stands on a pedestal, hangs on the wall or sits
      // on the counter as a bowl (`sinkType`, 2026-10-25); a vanity's
      // undermount or drop-in is the base
      key: 'basin',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['pedestal', 'wall-mount', 'vessel'],
      words: {
        pedestal: 'pedestal',
        'wall-mount': 'wall-hung',
        vessel: 'vessel',
      },
      synonyms: [
        { value: 'pedestal', pattern: /\bpedestal\b/i },
        // only beside a sink: a wall-hung toilet is the mount's, a
        // wall-mount faucet the faucet's
        {
          value: 'wall-mount',
          pattern: new RegExp(
            `${WALL_HUNG.source}(?=.*${BASIN.source})|(?<=${BASIN.source}.*)${WALL_HUNG.source}`,
            'i'
          ),
        },
        { value: 'vessel', pattern: /\bvessel\b/i },
      ],
    },
    {
      // a kitchen sink whose apron front stands proud of the counter, the
      // cabinet's front cut for it (`subtype: 'farmhouse'`, 2026-10-27); an
      // undermount or drop-in is the base. A different sink to buy, so the
      // kitchen sink allowance has a rate for it.
      key: 'apron',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['farmhouse'],
      words: { farmhouse: 'farmhouse' },
      synonyms: [
        { value: 'farmhouse', pattern: /\bfarmhouse\b|\bapron[- ]front\b/i },
      ],
    },
    {
      // a kitchen sink of two bowls (`basinCount: 2`, 2026-10-27) — a
      // different sink to buy, a second strainer to hook up; one bowl is the
      // base
      key: 'bowls',
      effect: EFFECTS.EXTRA,
      ordered: false,
      source: 'object',
      values: ['double'],
      words: { double: 'double bowl' },
      synonyms: [
        {
          value: 'double',
          pattern: /\b(?:double|dual|two)[- ](?:bowl|bay|basin)s?\b/i,
        },
      ],
    },
    {
      // a top in a BATHROOM — a vanity's, a tub deck's — rather than a
      // kitchen's (2026-10-27): a contractor may price taking one out by the
      // foot and a kitchen's by the ft² (Garrett's "Demo the bathroom
      // countertop" / "Demo the countertops in the kitchen"). The same work,
      // so an unpriced bathroom top carries the countertop rate silently.
      key: 'top',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'scope',
      values: ['bath'],
      words: { bath: 'bathroom top' },
      // only beside a top: a plain "Vanity" line is the cabinet's, and a
      // bathroom's own lines are not its tops
      synonyms: [
        {
          value: 'bath',
          pattern: beside(
            TOP_CONTEXT,
            String.raw`\b(?:bath(?:room)?|vanity)\b`
          ),
        },
      ],
    },
    {
      // a system pan's size against its maker's standard ones (shower
      // systems step 4b): a size the maker does not make is CUSTOM, which a
      // contractor may price apart ("Sentrel Custom Pan" beside "Sentrel
      // Shower Pan"). A look: setting the pan is the same job; a book that
      // prices only the standard pan carries it silently.
      key: 'sizing',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'object',
      values: ['custom'],
      words: { custom: 'custom size' },
      synonyms: [{ value: 'custom', pattern: /\bcustom\b/i }],
    },
    {
      // WHOSE runs a relocation moved (2026-10-31): a vanity's sink, its
      // drain and supplies run in the wall it stands against, which a
      // contractor may price apart from a drain broken through the slab
      // ("Relocate Wall Drain and Supply Lines for Vanity", per foot it
      // moved). A look: a book with no such price carries its relocation
      // price to it silently, so nothing changes for anyone else.
      key: 'fixture',
      effect: EFFECTS.LOOK,
      ordered: false,
      source: 'relocation',
      values: ['vanity'],
      words: { vanity: 'vanity sink' },
      // only beside a moved drain or supply: "Vanity" alone is the cabinet,
      // the line's own noun, and a fact word is blanked out of a name
      synonyms: [
        {
          value: 'vanity',
          pattern: beside(
            RELOCATION_CONTEXT,
            String.raw`\bvanit(?:y|ies)\b|\blav(?:atory|atories|s)?\b`
          ),
        },
      ],
    },
  ].map(declare)
);

const BY_KEY = new Map(AXIS_DECLARATIONS.map((a) => [a.key, a]));

/** An axis's declaration, or null for a name that is not an axis. */
const axisOf = (key) => BY_KEY.get(key) ?? null;

module.exports = {
  EFFECTS,
  SOURCES,
  AXIS_DECLARATIONS,
  axisOf,
  SOLID_SURFACE,
};
